import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  BillableIcon,
  ClientLink,
  Currency,
  ExportDropdown,
  HoursProgress,
  InternalClientTooltip,
  ProjectLink,
  RevenueProgress,
  Tag,
  TagsList,
  Tooltip,
} from '~/components';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useAuth,
  useDocumentTitle,
  useSearchParams,
  useSearchParamsConfig,
  useClientFilters,
  useProjectFilters,
  useProjectRoleFilters,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { QuerySort } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersBar,
  ProjectRoleFiltersGroup,
  FilterButton,
} from '~/components/filters';

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

export default function ProjectRoles({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const searchParamsConfig = useSearchParamsConfig();

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters(() => ({
    projectRecordStatusId: { ...searchParamsConfig.projectRecordStatusId, default: 'active' },
  }));
  const projectRoleFilters = useProjectRoleFilters();

  const [params, setParams] = useState({
    sort: new QuerySort('project.name', 'asc'),
    ...projectRoleFilters.filters,
    ...clientFilters.filters,
    ...projectFilters.filters,
  });

  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        sort: { default: new QuerySort('project.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...projectRoleFilters.searchParamsConfig,
      }),
      [searchParamsConfig, clientFilters, projectFilters, projectRoleFilters],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  const urlSearchParams = useMemo(
    () => ({
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...projectRoleFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, projectRoleFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const auth = useAuth();

  const columns = {
    revenue: auth.projects.viewRevenue,
    rate: auth.projects.viewRates,
  };

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column width="3rem">
            <BillableIcon showTooltip={false} />
          </Table.Column>
          <Table.Column sticky minWidth="16rem" name="project.name" onSort={onSort} sort={params.sort}>
            Project/Client
          </Table.Column>
          <Table.Column width="10rem">Status</Table.Column>
          <Table.Column width="16rem">Assigned To</Table.Column>
          <Table.Column width="7rem" align="right" isVisible={columns.viewRevenue}>
            <Tooltip message="Revenue divided by hours worked, billable and non-billable, for the period">
              Effective Rate
            </Tooltip>
          </Table.Column>
          <Table.Column width="16rem">Hours</Table.Column>
          <Table.Column width="16rem" isVisible={columns.viewRevenue}>
            Services Revenue
          </Table.Column>
        </Table.Header>

        <Table.Body fade={query.status === 'sorting'}>
          {report.records.map((project) => {
            const currency = project.currency;

            return (
              <React.Fragment key={project.id}>
                <Table.GroupRow top="2.95rem">
                  <Table.Cell></Table.Cell>
                  <Table.Cell>
                    <ProjectInfo>
                      <p>
                        <ProjectLink project={project} />
                      </p>
                      <small>
                        <ClientLink client={project.client} />
                        {project.client.isInternal && <InternalClientTooltip />}
                      </small>
                    </ProjectInfo>
                  </Table.Cell>
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                </Table.GroupRow>

                {project.roles.map((role) => {
                  return (
                    <Table.Row key={role.id}>
                      <Table.Cell>
                        <BillableIcon value={role.isBillable} />
                      </Table.Cell>
                      <Table.Cell>
                        <div style={{ marginLeft: '1rem' }}>{role.name}</div>
                      </Table.Cell>
                      <Table.Cell>{role.isActive ? 'Active' : 'Inactive'}</Table.Cell>
                      <Table.Cell>
                        {role.members?.length > 0 && (
                          <TagsList style={{ fontSize: '0.75rem' }}>
                            {role.members.map(({ id, name }) => (
                              <Tag key={id}>{name}</Tag>
                            ))}
                          </TagsList>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {project.permissions.viewRates && <Currency value={role.effectiveRate} currency={currency} />}
                      </Table.Cell>
                      <Table.Cell>
                        {project.permissions.viewTimeAndExpenses && (
                          <HoursProgress worked={role.hours} estimated={role.budgetTotalHours} />
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {project.permissions.viewRevenue && project.permissions.viewRates && (
                          <RevenueProgress
                            earned={role.revenue}
                            budgeted={role.budgetTotalRevenue}
                            currency={currency}
                          />
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>

      <Table.Status
        total={report.records.map((project) => project.roles).flat().length}
        label="Role"
        isLoading={query.status !== 'ready'}
        partial={report.partialResults}
      />
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
